/**
 * Push Notification Hook
 * Handles service worker registration and push subscription management
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '@/config';

const API = `${API_URL}/api`;

// Convert base64 string to Uint8Array for push subscription
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function usePushNotifications() {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [permission, setPermission] = useState('default');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if push notifications are supported
  useEffect(() => {
    const checkSupport = async () => {
      const supported = 'serviceWorker' in navigator && 'PushManager' in window;
      setIsSupported(supported);
      
      if (supported) {
        setPermission(Notification.permission);
        
        // Check for existing subscription
        try {
          const registration = await navigator.serviceWorker.ready;
          const existingSubscription = await registration.pushManager.getSubscription();
          
          if (existingSubscription) {
            setSubscription(existingSubscription);
            setIsSubscribed(true);
          }
        } catch (err) {
          console.error('Error checking subscription:', err);
        }
      }
      
      setIsLoading(false);
    };
    
    checkSupport();
  }, []);

  // Register service worker
  const registerServiceWorker = useCallback(async () => {
    if (!('serviceWorker' in navigator)) {
      throw new Error('Service workers not supported');
    }
    
    try {
      const registration = await navigator.serviceWorker.register('/sw.js');
      console.log('Service Worker registered:', registration);
      return registration;
    } catch (err) {
      console.error('Service Worker registration failed:', err);
      throw err;
    }
  }, []);

  // Subscribe to push notifications
  const subscribe = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Request permission if needed
      if (Notification.permission === 'default') {
        const result = await Notification.requestPermission();
        setPermission(result);
        
        if (result !== 'granted') {
          throw new Error('Notification permission denied');
        }
      } else if (Notification.permission === 'denied') {
        throw new Error('Notifications are blocked. Please enable them in browser settings.');
      }
      
      // Get VAPID public key from server
      const { data: keyData } = await axios.get(`${API}/push/vapid-public-key`);
      const vapidPublicKey = keyData.public_key;
      
      if (!vapidPublicKey) {
        throw new Error('Push notifications not configured on server');
      }
      
      // Register service worker
      const registration = await registerServiceWorker();
      await navigator.serviceWorker.ready;
      
      // Subscribe to push manager
      const pushSubscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });
      
      // Send subscription to server
      const token = localStorage.getItem('session_token');
      await axios.post(`${API}/push/subscribe`, {
        endpoint: pushSubscription.endpoint,
        keys: {
          p256dh: btoa(String.fromCharCode(...new Uint8Array(pushSubscription.getKey('p256dh')))),
          auth: btoa(String.fromCharCode(...new Uint8Array(pushSubscription.getKey('auth'))))
        },
        expirationTime: pushSubscription.expirationTime
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      setSubscription(pushSubscription);
      setIsSubscribed(true);
      
      return pushSubscription;
    } catch (err) {
      console.error('Subscription error:', err);
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [registerServiceWorker]);

  // Unsubscribe from push notifications
  const unsubscribe = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      if (subscription) {
        // Unsubscribe from push manager
        await subscription.unsubscribe();
        
        // Remove from server
        const token = localStorage.getItem('session_token');
        await axios.delete(`${API}/push/unsubscribe`, {
          params: { endpoint: subscription.endpoint },
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
      }
      
      setSubscription(null);
      setIsSubscribed(false);
    } catch (err) {
      console.error('Unsubscribe error:', err);
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [subscription]);

  // Toggle subscription
  const toggleSubscription = useCallback(async () => {
    if (isSubscribed) {
      await unsubscribe();
    } else {
      await subscribe();
    }
  }, [isSubscribed, subscribe, unsubscribe]);

  return {
    isSupported,
    isSubscribed,
    subscription,
    permission,
    isLoading,
    error,
    subscribe,
    unsubscribe,
    toggleSubscription
  };
}

export default usePushNotifications;
